import { Link } from 'react-router-dom';

import { MenuIcon } from '@/components/MenuIcon';
import { useHrProfile } from '@/pages/home/useHrProfile';
import { useMyProfile } from '@/pages/home/useMyProfile';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * 인사 정보. 잔여 연차와 자격증·라이센스 갱신 정보를 한 칸에 모은다.
 *
 * <b>급여·인사고과는 여기서 바로 보이지 않는다.</b> 홈 대시보드는 옆 사람에게 어깨너머로 보이기 쉬운
 * 화면이라, 급여와 고과 같은 민감정보는 감추고 마이페이지에서만 열게 한다(마이페이지는 본인이 의도적으로
 * 들어가는 화면이다). 잔여 연차와 자격증은 민감도가 낮아 그대로 둔다.
 *
 * 자료는 {@link useHrProfile} 가 서버에서 읽는다 — 마이페이지와 같은 훅이라 요청은 한 번만 나간다.
 */

/** 만료가 이 일수 안이면 갱신을 챙겨야 한다. */
const EXPIRY_SOON_DAYS = 90;

export function HrInfoWidget() {
  const profile = useHrProfile();
  // 소속·발령은 다른 훅이 읽는다(`/v1/home/profile`) — 홈의 여러 위젯이 이미 그 하나를 함께 본다.
  const { allowance } = useMyProfile();

  const leave = profile.data?.leave ?? null;
  const licenses = profile.data?.licenses ?? [];
  const software = profile.data?.software ?? [];
  /*
    급여·고과는 값이 아예 내려오지 않는다(마이페이지에서 비밀번호를 다시 받아야 열린다). 서버가
    주는 것은 「있는지」 와 <b>「새 것인지」</b> 뿐이고, 줄을 세우는 판단은 뒤쪽이 한다.

    빈 위젯 판정도 <b>실제로 그리는 것</b>을 세야 한다 — `has*` 로 세면 다 읽은 사람의 위젯이
    「비어 있지 않다」 고 판정돼 아무것도 없는 칸이 남는다.
  */
  // 급여명세서 줄이 내려가면서(2026-09-22) `payslipUnread` 는 여기서 빠졌다 — 그리지 않는 것을
  // 세면 <b>아무것도 없는 위젯이 「비어 있지 않다」</b> 로 판정돼 빈 칸이 남는다.
  const unreadSensitive = Boolean(profile.data?.evaluationUnread);
  const empty = !leave && licenses.length === 0 && software.length === 0 && !unreadSensitive
    && !(allowance?.eligible && allowance.unread);

  return (
    <WidgetFrame
      title="인사 정보"
      icon="user"
      to="/mypage"
      toLabel="마이페이지"
      pending={profile.isPending}
      error={profile.error}
      empty={empty}
      emptyText="등록된 연차·자격증 정보가 없습니다."
    >
      <div className="space-y-3">
        {/* 잔여 연차 */}
        {leave && (
          <div className="flex items-baseline gap-2">
            <span className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>{leave.year} 잔여 연차</span>
            <span className="ml-auto flex items-baseline gap-1">
              <span className="font-bold text-ui-2xl" style={{ color: 'var(--accent)', lineHeight: 1 }}>
                {formatDays(leave.remainingDays)}
              </span>
              <span className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>/ {formatDays(leave.totalDays)}일</span>
            </span>
          </div>
        )}

        {/*
          수당 대상 — 인사발령이 확정되면 여기 뜬다. 금액은 없다(대상 여부까지만, DOC-10 §A).
          연차 다음 자리인 이유: 파견 나간 사람에게 이것은 자격증보다 먼저 확인하고 싶은 값이다.
        */}
        {allowance?.eligible && allowance.unread && (
          /*
            <b>누르면 그 화면으로 간다</b>(사용자 지시 2026-08-27). 「수당 대상」 이라고 알려 주기만
            하고 어디서 자세히 보는지 말하지 않으면, 사람은 마이페이지에서 탭을 하나씩 열어 보다 만다.
            아래 급여·고과 줄이 이미 같은 모양(줄 전체가 링크)이라 새로 배울 것도 없다.
          */
          <Link
            to="/mypage?tab=welfare&sub=allowance"
            className="flex items-center gap-2 rounded-lg px-3 py-2"
            style={{ border: '1px solid var(--border)', background: 'var(--accent-bg)' }}
          >
            <span className="badge badge-blue badge-sm">수당 대상</span>
            <span className="min-w-0 flex-1 truncate text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
              {allowance.unitName}
              {allowance.note ? ` · ${allowance.note}` : ''}
            </span>
            <span className="flex-shrink-0 text-ui-xs" style={{ color: 'var(--accent)' }}>
              자세히 ›
            </span>
          </Link>
        )}

        {/*
          인사고과 — 민감정보라 값은 감추고 <b>그 탭으로</b> 보낸다.

          급여명세서와 한 줄로 묶어 두었더니 둘 중 하나만 있는 사람에게도 둘 다 있는 것처럼 읽혔고,
          눌러도 마이페이지 첫 탭이 열려 <b>거기서 다시 탭을 찾아야</b> 했다. 이제 <b>있는 것만</b>
          한 줄이 서고 그 탭으로 곧장 간다(사용자 지시 2026-08-27).

          급여명세서 줄은 화면과 함께 내려갔다(2026-09-22). 고과는 인사 탭 안으로 들어가
          `?tab=career` 다 — 링크가 탭 이름을 들고 있으므로 탭이 옮겨 가면 여기도 함께 옮긴다.

          {profile.data?.payslipUnread && (
            <SensitiveLink to="/mypage?tab=payslip" label="새 급여명세서" />
          )}
        */}
        {profile.data?.evaluationUnread && (
          <SensitiveLink to="/mypage?tab=career" label="새 인사고과" />
        )}

        {/* 자격증·라이센스 */}
        <div>
          <p className="mb-1 text-ui-xs" style={{ fontWeight: 600, color: 'var(--text-muted)' }}>
            자격증 · 라이센스
          </p>
          {licenses.length === 0 ? (
            <p className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>등록된 자격증이 없습니다.</p>
          ) : (
            <div className="space-y-0.5">
              {licenses.map((license) => (
                <div key={license.name} className="flex items-center gap-2">
                  <span className="flex-1 truncate text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                    {license.name}
                  </span>
                  <ExpiryBadge expiryDate={license.expiryDate} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/*
          지급받은 소프트웨어 — 자격증과 <b>다른 묶음</b>이다. 자격증은 본인이 딴 것이고 이것은
          회사가 준 것이라, 만료가 다가올 때 움직여야 하는 손이 다르다(총무가 갱신한다).

          <b>키는 여기 없다</b>. 값은 본인 비밀번호를 다시 넣어야 열리므로(V266) 그 문이 있는
          마이페이지로 보낸다 — 홈은 옆 사람에게 어깨너머로 보이는 화면이다.

          받은 것이 없으면 줄 자체를 세우지 않는다. 「배포된 라이센스가 없습니다」 는 캐드를 쓰지
          않는 사람에게 매일 서 있을 문장이다.
        */}
        {software.length > 0 && (
          <div>
            <div className="mb-1 flex items-center gap-2">
              <p className="text-ui-xs" style={{ fontWeight: 600, color: 'var(--text-muted)' }}>
                소프트웨어 라이센스
              </p>
              <Link
                to="/mypage?tab=welfare&sub=asset"
                className="ml-auto flex-shrink-0 text-ui-xs"
                style={{ color: 'var(--accent)' }}
              >
                자세히 ›
              </Link>
            </div>
            <div className="space-y-0.5">
              {software.map((item, index) => (
                <div key={`${item.productName}-${item.edition ?? ''}-${index}`} className="flex items-center gap-2">
                  <span className="flex-1 truncate text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                    {item.productName}
                    {item.edition ? ` ${item.edition}` : ''}
                  </span>
                  <ExpiryBadge expiryDate={item.expiresOn} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </WidgetFrame>
  );
}

/**
 * 값을 감춘 채 <b>그 탭으로 보내는</b> 한 줄. 급여명세서·인사고과가 같은 모양을 쓴다.
 *
 * <p>자물쇠를 그대로 둔다 — 눌러도 바로 열리지 않고 <b>비밀번호를 한 번 더</b> 받는다는 사실을
 * 미리 알려 준다. 그것을 모르고 누르면 갑작스러운 비밀번호 창이 고장처럼 보인다.
 */
function SensitiveLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-slate-50"
      style={{ border: '1px solid var(--border)' }}
    >
      <span style={{ color: 'var(--text-muted)' }}>
        <MenuIcon name="lock" size={14} />
      </span>
      <span className="min-w-0 flex-1 text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </span>
      <span className="flex-shrink-0 text-ui-xs" style={{ color: 'var(--accent)' }}>
        자세히 ›
      </span>
    </Link>
  );
}

/** 만료일이 없으면 '무기한', 지났으면 '만료', 임박하면 'D-n', 그 외에는 만료연월. */
function ExpiryBadge({ expiryDate }: { expiryDate: string | null }) {
  if (!expiryDate) {
    return <span className="badge badge-gray badge-sm">무기한</span>;
  }

  const days = daysUntil(expiryDate);
  if (days < 0) {
    return <span className="badge badge-red badge-sm">만료</span>;
  }
  if (days <= EXPIRY_SOON_DAYS) {
    return <span className="badge badge-red badge-sm">갱신 D-{days}</span>;
  }
  return (
    <span className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>~{expiryDate.slice(0, 7).replace('-', '.')}</span>
  );
}

function formatDays(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** 오늘부터 대상일까지 남은 일수(하루 단위). */
function daysUntil(dateOnly: string): number {
  const target = new Date(`${dateOnly}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
